import React from 'react';
import { Button, Space, Tag, Typography, Tooltip } from 'antd';
import { EditOutlined, FileOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

/**
 * Get table columns configuration for shop management table
 * @param {Object} options - Configuration options
 * @param {Function} options.onReadFile - Handler for reading shop files
 * @param {Function} options.onManageUser - Handler for managing/editing user
 * @returns {Array} Ant Design Table columns configuration
 */
export const getShopTableColumns = ({ onReadFile, onManageUser }) => [
  {
    title: '#',
    key: 'index',
    width: 50,
    render: (_, __, index) => index + 1,
  },
  {
    title: 'รหัสร้านค้า / วันที่สมัคร',
    key: 'shop_id_createdate',
    width: 150,
    sorter: (a, b) => dayjs(a.createdate || 0).unix() - dayjs(b.createdate || 0).unix(),
    defaultSortOrder: 'descend',
    render: (_, record) => (
      <Space direction='vertical' size={0}>
        <Text strong>{record.shop_id || '-'}</Text>
        <Text type='secondary' style={{ fontSize: '11px' }}>
          🕒 {record.createdate ? dayjs(record.createdate).format('DD/MM/YYYY HH:mm') : '-'}
        </Text>
      </Space>
    ),
  },
  {
    title: 'ชื่อร้านค้า / จังหวัด',
    key: 'shop_province',
    width: 170,
    sorter: (a, b) => a.shop_name?.localeCompare(b.shop_name),
    render: (_, record) => (
      <Space direction='vertical' size={0}>
        <Text strong>{record.shop_name || '-'}</Text>
        {record.shop_province && record.shop_province !== '-' ? (
          <Text type='secondary' style={{ fontSize: '12px' }}>
            📍 {record.shop_province}
          </Text>
        ) : null}
      </Space>
    ),
  },
  {
    title: 'เจ้าของร้าน / ข้อมูลติดต่อ',
    key: 'owner_contact',
    width: 280,
    sorter: (a, b) => a.shop_firstname?.localeCompare(b.shop_firstname),
    render: (_, record) => {
      const fullName = `${record.shop_firstname || ''} ${record.shop_lastname || ''}`.trim();
      const hasPhone = record.shop_telno && record.shop_telno !== '-';
      const hasEmail = record.shop_email && record.shop_email !== '-';

      return (
        <Space direction='vertical' size={0} style={{ maxWidth: '100%' }}>
          <div>
            <Text strong>{fullName || record.shop_username || '-'}</Text>
            {fullName && record.shop_username && (
              <Text type='secondary' style={{ fontSize: '12px', marginLeft: '6px' }}>
                ({record.shop_username})
              </Text>
            )}
          </div>
          <Text type='secondary' style={{ fontSize: '12px' }}>
            {hasPhone ? `📞 ${record.shop_telno}` : ''}
            {hasPhone && hasEmail ? ' • ' : ''}
            {hasEmail ? `✉️ ${record.shop_email}` : ''}
            {!hasPhone && !hasEmail ? '-' : ''}
          </Text>
        </Space>
      );
    },
  },
  {
    title: 'บัญชีธนาคาร',
    key: 'bank',
    width: 150,
    render: (_, record) => {
      const hasBankName = record.bank_name && record.bank_name !== '-';
      const hasBankAcc = record.bank_acc_id && record.bank_acc_id !== '-';

      if (!hasBankName && !hasBankAcc) {
        return <Text type='secondary'>-</Text>;
      }

      return (
        <Space direction='vertical' size={0}>
          <Text strong>{hasBankName ? record.bank_name : '-'}</Text>
          {hasBankAcc && (
            <Text type='secondary' style={{ fontSize: '12px' }}>
              💳 {record.bank_acc_id}
            </Text>
          )}
        </Space>
      );
    },
  },
  {
    title: 'เอกสาร',
    dataIndex: 'shopdocument',
    key: 'shopdocument',
    width: 100,
    align: 'center',
    filters: [
      { text: 'ส่งเอกสารแล้ว (submited)', value: 'submited' },
      { text: 'ยังไม่ส่ง / รอเอกสาร (pending)', value: 'pending' },
    ],
    onFilter: (value, record) => {
      if (value === 'pending') {
        return !record.shopdocument || record.shopdocument === 'pending';
      }
      return record.shopdocument === value;
    },
    render: (text) => (
      <Tag
        color={text === 'submited' ? 'success' : 'default'}
        style={{ margin: 0, borderRadius: '4px' }}
      >
        {text === 'submited' ? 'ส่งแล้ว' : text || 'ยังไม่ส่ง'}
      </Tag>
    ),
  },
  {
    title: 'จัดการ',
    key: 'action',
    width: 140,
    align: 'center',
    fixed: 'right', // Pinned to right side during horizontal scroll for desktop usability
    render: (_, record) => (
      <Space style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Tooltip title={record.shopdocument === 'pending' ? 'ยังไม่มีเอกสาร — กดเพื่ออัพโหลด' : ''}>
          <Button
            icon={record.shopdocument === 'submited' ? <FileOutlined /> : <UploadOutlined />}
            size='small'
            style={
              record.shopdocument === 'pending' ? { borderColor: '#1890ff', color: '#1890ff' } : {}
            }
            onClick={() => onReadFile(record)}
          >
            {record.shopdocument === 'submited' ? 'ไฟล์' : 'อัพโหลด'}
          </Button>
        </Tooltip>
        <Button
          type='primary'
          icon={<EditOutlined />}
          size='small'
          onClick={() => onManageUser(record)}
        >
          แก้ไข
        </Button>
      </Space>
    ),
  },
];
